import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import ProviderDetailsPage from './ProviderDetailsPage';
import SidebarPanel from '../../components/admin/SidebarPanel';
import AgGridTable from '../../components/AgGridTable';

const ProviderManagementPage = () => {
  const navigate = useNavigate();
  const { providerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [healthSystemFilter, setHealthSystemFilter] = useState('');
  const [healthSystems, setHealthSystems] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  // Default to showing filters for better discoverability and immediate access to filtering options.
  // Users can toggle visibility via the "Hide Filters" button, and the state persists via URL parameter.
  // Can be controlled via ?filters=hidden URL parameter to start with filters collapsed.
  const [showFilters, setShowFilters] = useState(
    searchParams.get('filters') !== 'hidden'
  );
  const [showDetailsSidebar, setShowDetailsSidebar] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    // Fetch health systems and specialties for filter dropdowns
    const fetchData = async () => {
      try {
        const healthSystemsResponse = await adminApi.getHealthSystems();
        if (healthSystemsResponse.data && healthSystemsResponse.data.success) {
          setHealthSystems(healthSystemsResponse.data.health_systems);
        }

        const specialtiesResponse = await adminApi.getSpecialties(); // Assuming an API call for specialties
        if (specialtiesResponse.data && specialtiesResponse.data.success) {
          setSpecialties(specialtiesResponse.data.specialties);
        }
      } catch (err) {
        console.error('Error fetching filter data:', err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        
        // Build filters object
        const filters = {};
        if (specialtyFilter) filters.specialty_id = specialtyFilter;
        if (healthSystemFilter) filters.health_system_id = healthSystemFilter;
        
        const response = await adminApi.getProviders(filters); // Assuming adminApi.getProviders

        if (response.data && response.data.success) {
          setProviders(response.data.providers);
        } else {
          setError(response.data?.message || 'Failed to fetch providers');
        }
      } catch (err) {
        console.error('Error fetching providers:', err);
        setError(err.message || 'An error occurred while fetching providers');
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [specialtyFilter, healthSystemFilter]);

  // Handle URL parameter - automatically open sidebar if providerId is in URL
  useEffect(() => {
    if (providerId && providers.length > 0 && !selectedProvider) {
      // Check both id (database ID like "PROVIDER-287") and provider_id (UUID)
      const provider = providers.find(p => p.id === providerId || p.provider_id === providerId);
      if (provider) {
        handleViewDetailsClick(provider);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, providers, selectedProvider]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Custom Cell Renderer for Actions column
  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    return (
      <div className="btn-group btn-group-sm" role="group">
        <button
          className="btn btn-outline-primary"
          onClick={() => handleViewDetailsClick(data)}
          title="View Details"
        >
          <i className="fas fa-eye me-1"></i>
          View
        </button>
      </div>
    );
  };

  // ag-Grid Column Definitions
  const [columnDefs] = useState([
    {
      headerName: 'Name',
      field: 'name',
      width: 180,
      valueGetter: (params) => {
        if (params.data) {
          return `${params.data.first_name || ''} ${params.data.last_name || ''}`.trim();
        }
        return '';
      },
      cellRenderer: (params) => {
        if (params.data) {
          return (
            <a
              href="#"
              className="text-body fw-bold"
              onClick={(e) => {
                e.preventDefault();
                handleViewDetailsClick(params.data);
              }}
            >
              {`${params.data.first_name} ${params.data.last_name}`}
            </a>
          );
        }
        return null;
      }
    },
    {
      headerName: 'Email',
      field: 'email',
      width: 200
    },
    {
      headerName: 'Phone',
      field: 'phone',
      width: 130
    },
    {
      headerName: 'Specialties',
      field: 'specialties',
      width: 180,
      valueGetter: (p) => p.data?.specialties?.map(s => s.name)?.join(', ') || 'N/A'
    },
    {
      headerName: 'Health System',
      field: 'health_system',
      width: 180,
      valueGetter: (params) => params.data?.health_system?.name || 'N/A',
      cellRenderer: (params) => {
        if (params.data?.health_system) {
          return (
            <Link
              to={`/admin/health-systems/${params.data.health_system.health_system_id}`}
              className="text-body fw-bold"
            >
              {params.data.health_system.name}
            </Link>
          );
        }
        return 'N/A';
      }
    },
    {
      headerName: 'Created',
      field: 'created_at',
      width: 180,
      valueGetter: (p) => formatDate(p.data?.created_at)
    },
    {
      headerName: 'Updated',
      field: 'updated_at',
      width: 180,
      valueGetter: (p) => formatDate(p.data?.updated_at)
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      minWidth: 120,
      sortable: false,
      filter: false
    }
  ]);

  const handleViewDetailsClick = (provider) => {
    setSelectedProvider(provider);
    setShowDetailsSidebar(true);
    // Navigate to URL with provider ID
    navigate(`/admin/providers/${provider.id}`);
  };

  const handleCloseSidebar = () => {
    setShowDetailsSidebar(false);
    setSelectedProvider(null);
    // Navigate back to base URL to remove the ID from URL
    navigate('/admin/providers');
  };

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Provider Management</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
                <li className="breadcrumb-item active">Providers</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <button
            className="btn btn-light"
            onClick={() => {
              const newShowFilters = !showFilters;
              setShowFilters(newShowFilters);
              // Update URL parameter to persist filter visibility preference
              const newParams = new URLSearchParams(searchParams);
              if (newShowFilters) {
                newParams.delete('filters');
              } else {
                newParams.set('filters', 'hidden');
              }
              setSearchParams(newParams, { replace: true });
            }}
          >
            <i className="fas fa-filter me-1"></i> {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filters Section */}
      {showFilters && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Filters</h5>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="healthSystemFilter" className="form-label">Health System</label>
                <select
                  id="healthSystemFilter"
                  className="form-select"
                  value={healthSystemFilter}
                  onChange={(e) => setHealthSystemFilter(e.target.value)}
                >
                  <option value="">All Health Systems</option>
                  {healthSystems.map(hs => (
                    <option key={hs.id} value={hs.health_system_id}>
                      {hs.name}
                    </option>
                  ))}
                </select>
                {/* TODO: Implement logic to pre-set this filter based on user's permissions/associated health system */}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="specialtyFilter" className="form-label">Specialty</label>
                <select
                  id="specialtyFilter"
                  className="form-select"
                  value={specialtyFilter}
                  onChange={(e) => setSpecialtyFilter(e.target.value)}
                >
                  <option value="">All Specialties</option>
                  {specialties.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSpecialtyFilter('');
                  setHealthSystemFilter('');
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Providers Table */}
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading providers...</p>
            </div>
          ) : providers.length === 0 ? (
            <div className="alert text-center text-secondary">
              No providers found. {specialtyFilter || healthSystemFilter ? 'Try adjusting your filters.' : ''}
            </div>
          ) : (
            <AgGridTable
              rowData={providers}
              columnDefs={columnDefs}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
                sortable: true,
                filter: true,
              }}
              gridOptions={{
                autoSizeStrategy: {
                  type: 'fitGridWidth'
                }
              }}
              getRowId={(params) => `${params.data.id}`}
            />
          )}
        </div>
      </div>

      <SidebarPanel
        isOpen={showDetailsSidebar}
        onClose={handleCloseSidebar}
        title="Provider Details"
      >
        {selectedProvider && (
          <ProviderDetailsPage
            providerId={selectedProvider.id}
            isEmbedded={true}
            onClose={handleCloseSidebar}
          />
        )}
      </SidebarPanel>
    </>
  );
};

export default ProviderManagementPage;